// Oct 3 2026 (Randy, stale-pricing cleanup — second pass) — live Creator Pro
// SKU prices, from GET /api/creator-pro/catalog.
//
// Why this exists: two components quoted the AI YouTube Replies add-on as a
// hardcoded "$50". That number is `day1_cents` on the `yt_ai_replies` node of
// the Creator Pro catalog, which Randy edits in Super Admin → Creator Pro. A
// price typed into JSX cannot follow that slider, and this is the same class
// of bug as the "$10/mo" that survived three months past a price change.
//
// Contract matches usePublicPricing deliberately: an unknown price returns
// `null` so the caller renders words instead of a number it cannot verify.
// Never a remembered default.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

let _cache = null;
let _cachedAt = 0;
const TTL_MS = 5 * 60 * 1000;

// The catalog is a tree: { suites: [ { children: [ { key, day1_cents } ] } ] }.
function flattenPrices(node, out = {}) {
  if (!node || typeof node !== "object") return out;
  for (const child of node.suites || []) flattenPrices(child, out);
  for (const child of node.children || []) flattenPrices(child, out);
  if (node.key && node.day1_cents != null) out[node.key] = node.day1_cents;
  return out;
}

export function useSkuPrice(skuKey) {
  const [cents, setCents] = useState(() =>
    (_cache && Date.now() - _cachedAt < TTL_MS ? _cache[skuKey] ?? null : null));

  useEffect(() => {
    if (_cache && Date.now() - _cachedAt < TTL_MS) {
      setCents(_cache[skuKey] ?? null);
      return;
    }
    let cancel = false;
    api.get("/creator-pro/catalog")
      .then((r) => {
        if (cancel) return;
        _cache = flattenPrices(r.data?.catalog || {});
        _cachedAt = Date.now();
        setCents(_cache[skuKey] ?? null);
      })
      .catch(() => { /* stay null — the caller shows words, not a guess */ });
    return () => { cancel = true; };
  }, [skuKey]);

  return cents;
}
